import CartographerReporter, {
  THIRTY_SECONDS,
  RouteInfo,
  NavigationAction,
} from './CartographerReporter';
import SoftNavLcpObserver from './SoftNavLcpObserver';
import { captureException } from '../ravenUtils';
import { ReporterAction } from '../types';
import { onVisibilityHidden } from '../visibility';
import { ReporterOptions } from './BaseReporter';
import PerformanceEventBus from '../PerformanceEventBus';
import PageStateTracker from '../PageStateTracker';
import {
  getElementSelector,
  getNearestComponentName,
} from '../elementSelector';
import { isInIframe } from '../browserAccessors';

export default class VitalsReporter extends CartographerReporter {
  private static readonly WINDOW_KEY = '__rhumb_vitals_reporter__';

  /**
   * Get the singleton instance of VitalsReporter.
   * Uses window-scoped storage so the instance is shared across all JS bundles
   * on the same page, preventing duplicate LCP reports.
   */
  public static getInstance(options?: ReporterOptions): VitalsReporter {
    const w = window as any;
    if (!w[VitalsReporter.WINDOW_KEY]) {
      w[VitalsReporter.WINDOW_KEY] = new VitalsReporter(options);
    }
    return w[VitalsReporter.WINDOW_KEY];
  }

  public static resetInstance(): void {
    (window as any)[VitalsReporter.WINDOW_KEY] = undefined;
  }

  lastRouteInfo: RouteInfo | undefined;
  navStartTime: number;
  stopped: boolean;
  flushQueueTimeout: number | undefined;
  lastStatus: NavigationAction['status'] | undefined;
  lastFailureType: NavigationAction['failureType'] | undefined;
  private interactionCleanupFunctions: (() => void)[];
  private softNavLcpObserver: SoftNavLcpObserver;

  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();

    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline,
    };
  }

  private debounce() {
    clearTimeout(this.flushQueueTimeout);
    this.flushQueueTimeout = setTimeout(() => {
      this.flushPerformanceQueue();
    }, THIRTY_SECONDS);
  }

  private flushSoftNavLcp() {
    try {
      const candidate = this.softNavLcpObserver.stop();
      if (candidate) {
        // Attribution depends on the call site:
        //   - Interaction / page-hide: lastRouteInfo is the destination route (set by
        //     ROUTE_SUCCEEDED), which is correct — the observer ran while that route
        //     was displayed.
        //   - ROUTE_STARTED: lastRouteInfo is still the *previous* route (ROUTE_SUCCEEDED
        //     hasn't fired yet), which is also correct — this flushes the LCP measured
        //     while the user was on the route being left.
        this.pushPerformanceAction(this.lastRouteInfo, 'soft-nav-lcp', {
          ...candidate,
          status: this.lastStatus,
          failureType: this.lastFailureType,
          isEmbedded: isInIframe(),
          pageState: this.getPageState(),
        });
        this.debounce();
      }
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  }

  private armSoftNavFlushListeners() {
    this.interactionCleanupFunctions.forEach((cleanup) => cleanup());
    this.interactionCleanupFunctions = [];

    const eventTypes: Array<
      'keydown' | 'mousedown' | 'pointerdown' | 'touchstart'
    > = ['keydown', 'mousedown', 'pointerdown', 'touchstart'];

    eventTypes.forEach((eventType) => {
      const handler = () => {
        this.flushSoftNavLcp();
        this.interactionCleanupFunctions.forEach((cleanup) => cleanup());
      };

      window.addEventListener(eventType, handler, {
        capture: true,
        once: true,
      });

      this.interactionCleanupFunctions.push(() =>
        window.removeEventListener(eventType, handler, { capture: true })
      );
    });
  }

  private constructor(options?: ReporterOptions) {
    super(options);
    this.stopped = false;
    this.lastStatus = undefined;
    this.lastFailureType = undefined;
    this.navStartTime = 0;
    this.interactionCleanupFunctions = [];
    this.softNavLcpObserver = new SoftNavLcpObserver(
      getElementSelector,
      getNearestComponentName
    );

    PerformanceEventBus.getInstance().subscribe(
      'long-animation-frame',
      (perfEntry) => {
        try {
          // For each script in the long animation frame, emit a longtask event
          // Only log scripts with duration >= 50ms
          perfEntry.scripts.forEach((script) => {
            if (script.duration >= 50) {
              const scriptData = script.toJSON();

              this.pushPerformanceAction(this.lastRouteInfo, 'longtask', {
                name: script.windowAttribution,
                ...scriptData,
                entryType: 'longtask',
                pageState: this.getPageState(),
              });
            }
          });
          this.debounce();
        } catch (e) {
          captureException(e instanceof Error ? e : new Error(String(e)));
        }
      }
    );

    this.armSoftNavFlushListeners();

    // Handle visibility change to flush remaining entries
    onVisibilityHidden(() => {
      try {
        this.flushSoftNavLcp();
        // Clean up interaction listeners since soft-nav LCP is now flushed
        this.interactionCleanupFunctions.forEach((cleanup) => cleanup());
        // Flush any remaining performance actions
        this.flushPerformanceQueue();
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  destroy() {
    // VitalsReporter is a singleton shared across all RhumbProvider instances;
    // its listeners must persist for the page lifetime.
  }

  report(action: ReporterAction) {
    if (
      action.type === 'COMPONENT_RENDERED' ||
      this.isResolved(action.payload.entry.id) ||
      this.stopped
    ) {
      return;
    }

    switch (action.type) {
      case 'ROUTE_STARTED': {
        this.navStartTime = action.payload.entry.timestamp;
        // Flush any soft-nav LCP accumulated for the route being left
        this.flushSoftNavLcp();
        // Start custom observer only for genuine soft navs (lastRouteInfo set = a route
        // has already resolved, so this is not the initial page load)
        if (this.lastRouteInfo) {
          this.softNavLcpObserver.start(this.navStartTime);
        }
        this.armSoftNavFlushListeners();
        break;
      }
      case 'ROUTE_TIMEOUT_EXPIRED': {
        const { routeSpec } = action.payload;
        if (routeSpec) {
          this.lastRouteInfo = { route: routeSpec.route };
        }
        this.lastStatus = 'failure';
        this.lastFailureType = 'watchdogExpired';
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_FAILED': {
        const {
          entry: { pathname },
          routeSpec,
        } = action.payload;
        if (routeSpec) {
          this.lastRouteInfo = {
            pathname,
            route: routeSpec.route,
          };
        }
        this.lastStatus = 'failure';
        this.lastFailureType =
          action.type === 'ROUTE_FAILED' ? 'errorSelector' : undefined;
        break;
      }
      case 'ROUTE_PARTIAL_SUCCESS': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'partial_success';
        this.lastFailureType = undefined;
        break;
      }
      case 'ROUTE_SUCCEEDED': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'success';
        this.lastFailureType = undefined;
        break;
      }
      default:
    }

    switch (action.type) {
      case 'ROUTE_UNEXPECTED': {
        this.stopped = true;
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED': {
        this.markResolved(action.payload.entry.id);
        break;
      }
      default:
    }
  }
}
